import { FC, ReactNode, createContext, useContext, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import {
  TurnkeyProvider as TurnkeySDKProvider,
  TurnkeyProviderConfig,
  useTurnkey,
  AuthState,
  KeyFormat,
} from '@turnkey/react-wallet-kit';
// Import CSS as raw text to completely bypass PostCSS/Tailwind processing
import turnkeyStyles from '@turnkey/react-wallet-kit/styles.css?raw';
import { Buffer } from 'buffer';
import bs58 from 'bs58';
import { PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { addBreadcrumb, setUser, clearUser, captureError } from '@/lib/sentry';
// Helius Sender is for trading (buy/sell) only — not used in sendTransaction.
import { sendViaSender } from '@/lib/trading/sender';

// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;
import { 
  getTurnkeyPublicConfig, 
  getTurnkeyMissingKeys, 
  isTurnkeyConfigured as checkTurnkeyConfigured,
  getTurnkeyDiagnostics,
} from '@/config/turnkeyPublicConfig';

// Inject Turnkey styles into DOM as a style tag (avoids Tailwind @layer conflict)
if (typeof document !== 'undefined') {
  const existingStyle = document.querySelector('style[data-turnkey-styles]');
  if (!existingStyle) {
    const style = document.createElement('style');
    style.setAttribute('data-turnkey-styles', 'true');
    style.textContent = turnkeyStyles;
    document.head.appendChild(style);
  }
}

// Re-export for external use
export const isTurnkeyConfigured = checkTurnkeyConfigured;

// Solana wallet account configuration for new users
const SOLANA_WALLET_ACCOUNT = {
  curve: 'CURVE_ED25519' as const,
  pathFormat: 'PATH_FORMAT_BIP32' as const,
  path: `m/44'/501'/0'/0'`,
  addressFormat: 'ADDRESS_FORMAT_SOLANA' as const,
};

// Default sub-org params to create Solana wallet on signup
const defaultSuborgParams = {
  customWallet: {
    walletName: 'Solana Wallet',
    walletAccounts: [SOLANA_WALLET_ACCOUNT],
  },
};

// Solana wallet info extracted from Turnkey wallets
export interface SolanaWalletInfo {
  walletId: string;
  walletName: string;
  account: any; // WalletAccount from Turnkey
  address: string;
  isImported: boolean;
}

// Wallet context interface - provides compatibility with previous wallet adapter usage
interface TurnkeyWalletContextType {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  isConfigured: boolean;
  
  // Wallet info
  publicKey: PublicKey | null;
  walletAddress: string | null;
  connected: boolean;
  
  // Multi-wallet support
  allSolanaWallets: SolanaWalletInfo[];
  activeWalletIndex: number;
  setActiveWallet: (index: number) => void;
  
  // Actions
  login: () => void;
  logout: () => Promise<void>;
  
  // Transaction signing (placeholder - Turnkey handles this differently)
  signTransaction: ((tx: Transaction | VersionedTransaction) => Promise<Transaction | VersionedTransaction>) | undefined;
  signAllTransactions: ((txs: (Transaction | VersionedTransaction)[]) => Promise<(Transaction | VersionedTransaction)[]>) | undefined;
  signMessage: ((message: Uint8Array) => Promise<Uint8Array>) | undefined;
  // sendTransaction matches the Solana wallet adapter signature for compatibility
  sendTransaction: ((tx: Transaction | VersionedTransaction, connection?: unknown) => Promise<string>) | undefined;
  // sendTradingTransaction routes through Helius Sender for MEV protection + Jito dual routing.
  // Used for buy/sell trades ONLY. Token creation uses sendTransaction (standard RPC).
  sendTradingTransaction: ((tx: Transaction | VersionedTransaction, connection?: unknown) => Promise<string>) | undefined;

  // Export wallet (private key reveal)
  exportWallet: (() => Promise<void>) | undefined;
  
  // Import wallet (private key import)
  importWallet: (() => Promise<string>) | undefined;
  
  // Callback for when wallet changes (for cache invalidation)
  onWalletChange: ((callback: () => void) => () => void) | undefined;
}

const TurnkeyWalletContext = createContext<TurnkeyWalletContextType | null>(null);

// Internal component that uses the Turnkey SDK hook
const TurnkeyWalletInner: FC<{ children: ReactNode }> = ({ children }) => {
  const {
    authState,
    handleLogin,
    logout: turnkeyLogout,
    wallets,
    user,
    handleSignMessage: turnkeySignMessage,
    signTransaction: turnkeySignTransaction, // Native transaction signing with TRANSACTION_TYPE_SOLANA
    refreshWallets,
    handleExportWalletAccount,
    handleImportPrivateKey,
  } = useTurnkey();

  const [isLoading, setIsLoading] = useState(true);
  const [walletChangeCallbacks, setWalletChangeCallbacks] = useState<Set<() => void>>(new Set());
  const isConfigured = isTurnkeyConfigured();

  // Check auth state on mount
  useEffect(() => {
    // Give Turnkey a moment to restore session (750ms balances
    // responsiveness with slow mobile connections where SDK init
    // can take 600ms+; authState effect below handles the happy path)
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 750);
    return () => clearTimeout(timer);
  }, []);

  // Update loading state when auth state changes
  useEffect(() => {
    if (authState !== undefined) {
      setIsLoading(false);
    }
  }, [authState]);

  const isAuthenticated = authState === AuthState.Authenticated;

  // Extract ALL Solana wallets from user's wallets
  const allSolanaWallets = useMemo((): SolanaWalletInfo[] => {
    if (!isAuthenticated || !wallets || wallets.length === 0) {
      return [];
    }

    const result: SolanaWalletInfo[] = [];
    for (const wallet of wallets) {
      if (wallet.accounts) {
        for (const account of wallet.accounts) {
          if (account.addressFormat === 'ADDRESS_FORMAT_SOLANA' && account.address) {
            result.push({
              walletId: wallet.walletId,
              walletName: wallet.walletName || 'Solana Wallet',
              account,
              address: account.address,
              isImported: wallet.imported === true,
            });
          }
        }
      }
    }
    return result;
  }, [isAuthenticated, wallets]);

  // Active wallet index with localStorage persistence (DEXTORO-LAUNCH-98: guard against null localStorage)
  const [activeWalletIndex, setActiveWalletIndexState] = useState(() => {
    try {
      const saved = typeof localStorage !== 'undefined' && localStorage
        ? localStorage.getItem('turnkey_active_wallet_index')
        : null;
      return saved ? parseInt(saved, 10) : 0;
    } catch {
      return 0;
    }
  });

  // Ensure active index is valid when wallets change
  useEffect(() => {
    if (allSolanaWallets.length > 0 && activeWalletIndex >= allSolanaWallets.length) {
      setActiveWalletIndexState(0);
      try { localStorage?.setItem('turnkey_active_wallet_index', '0'); } catch { /* localStorage unavailable */ }
    }
  }, [allSolanaWallets.length, activeWalletIndex]);

  // Set active wallet with persistence
  const setActiveWallet = useCallback((index: number) => {
    if (index >= 0 && index < allSolanaWallets.length) {
      setActiveWalletIndexState(index);
      try { localStorage?.setItem('turnkey_active_wallet_index', String(index)); } catch { /* localStorage unavailable */ }
      // Notify subscribers of wallet change
      walletChangeCallbacks.forEach(cb => cb());
    }
  }, [allSolanaWallets.length, walletChangeCallbacks]);

  // Get active wallet
  const activeWallet = allSolanaWallets[activeWalletIndex] || allSolanaWallets[0] || null;
  const solanaWalletAccount = activeWallet?.account ?? null;
  const solanaAddress = activeWallet?.address ?? null;

  // Convert address string to PublicKey
  const publicKey = useMemo(() => {
    if (!solanaAddress) return null;
    try {
      return new PublicKey(solanaAddress);
    } catch (e) {
      console.error('[TurnkeyProvider] Invalid Solana address:', solanaAddress, e);
      return null;
    }
  }, [solanaAddress]);

  // Login handler with config check
  const login = useCallback(() => {
    if (!isConfigured) {
      console.error('[TurnkeyProvider] Cannot login - Turnkey not configured');
      addBreadcrumb('Wallet login failed - not configured', 'wallet', { isConfigured });
      toast.error('Sign-in unavailable', {
        description: 'Turnkey authentication is not configured for this environment.',
        duration: 5000,
      });
      return;
    }
    DEBUG && console.log('[TurnkeyProvider] Opening login modal');
    addBreadcrumb('Wallet login initiated', 'wallet');
    handleLogin();
  }, [handleLogin, isConfigured]);

  // Logout handler - defensive check to avoid "No active session" error
  const logout = useCallback(async () => {
    // Only attempt logout if there's an active session
    if (!isAuthenticated) {
      DEBUG && console.log('[TurnkeyProvider] Skipping logout - no active session');
      return;
    }

    DEBUG && console.log('[TurnkeyProvider] Logging out');
    addBreadcrumb('Wallet logout initiated', 'wallet', { address: solanaAddress });

    try {
      await turnkeyLogout();
      addBreadcrumb('Wallet logout completed', 'wallet');
    } catch (err) {
      // Don't throw on logout errors - user is already trying to leave
      // "No active session found to log out from" is expected if session expired
      console.warn('[TurnkeyProvider] Logout error (suppressed):', err);
      addBreadcrumb('Wallet logout failed (suppressed)', 'wallet', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }, [turnkeyLogout, solanaAddress, isAuthenticated]);

  // Ensure wallets are fresh after authentication (helps avoid a brief "authenticated but no accounts" window)
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshWallets().catch((e) => {
      console.warn('[TurnkeyProvider] refreshWallets failed:', e);
      captureError(e instanceof Error ? e : new Error(String(e)), {
        feature: 'auth_wallet_refresh',
      });
    });
  }, [isAuthenticated, refreshWallets]);

  // Sign message using Turnkey (prefer UTF-8 when byte-roundtrip is safe)
  const signMessage = useCallback(async (message: Uint8Array): Promise<Uint8Array> => {
    if (!solanaWalletAccount) {
      throw new Error('Solana wallet not ready yet. Please wait a moment and try again.');
    }

    const toSignatureBytes = (result: any): Uint8Array => {
      DEBUG && console.log('[TurnkeyProvider] signMessage raw result:', {
        hasSignature: !!result?.signature,
        signatureLength: result?.signature?.length,
        hasR: !!result?.r,
        hasS: !!result?.s,
        rLength: result?.r?.length,
        sLength: result?.s?.length,
      });
      
      // Some Turnkey flows may return a full signature, others return r/s components.
      if (result?.signature) {
        const sig = decodeSignatureComponent(result.signature);
        DEBUG && console.log('[TurnkeyProvider] Decoded signature length:', sig.length);
        if (sig.length === 64) return sig;
        // If we got an unexpected length, log but try to continue with r/s
        console.warn('[TurnkeyProvider] Signature length not 64, trying r/s components');
      }

      if (result?.r && result?.s) {
        const rBytes = decodeSignatureComponent(result.r);
        const sBytes = decodeSignatureComponent(result.s);
        
        DEBUG && console.log('[TurnkeyProvider] r/s component lengths:', {
          rLength: rBytes.length,
          sLength: sBytes.length,
        });

        if (rBytes.length !== 32 || sBytes.length !== 32) {
          throw new Error(`Unexpected signature component sizes: r=${rBytes.length}, s=${sBytes.length}`);
        }

        const sig = new Uint8Array(64);
        sig.set(rBytes, 0);
        sig.set(sBytes, 32);
        return sig;
      }

      throw new Error('Invalid signature result from Turnkey - no signature or r/s components');
    };

    // For human-readable messages (like the username challenge), sign the UTF-8 string directly.
    // This avoids signing the hex-encoded representation (which can break verification).
    const decoded = new TextDecoder().decode(message);
    const reencoded = new TextEncoder().encode(decoded);
    const canUseUtf8 =
      reencoded.length === message.length && reencoded.every((b, i) => b === message[i]);

    DEBUG && console.log('[TurnkeyProvider] signMessage params:', {
      messageLength: message.length,
      canUseUtf8,
      messagePreview: decoded.slice(0, 50),
    });

    if (canUseUtf8) {
      // Use TEXT_UTF8 encoding explicitly for human-readable messages
      // HASH_FUNCTION_NOT_APPLICABLE is REQUIRED for Ed25519 (Solana) - RFC 8032 does not support configurable hashing
      const result = await turnkeySignMessage({
        message: decoded,
        walletAccount: solanaWalletAccount,
        encoding: 'PAYLOAD_ENCODING_TEXT_UTF8',
        hashFunction: 'HASH_FUNCTION_NOT_APPLICABLE',
      });
      return toSignatureBytes(result);
    }

    // Fallback: byte-safe raw payload signing for arbitrary bytes
    const hexMessage = bytesToHex(message);
    const result = await turnkeySignMessage({
      message: hexMessage,
      walletAccount: solanaWalletAccount,
      encoding: 'PAYLOAD_ENCODING_HEXADECIMAL',
      hashFunction: 'HASH_FUNCTION_NOT_APPLICABLE',
    });

    return toSignatureBytes(result);
  }, [solanaWalletAccount, turnkeySignMessage]);

  // Sign a Solana transaction using Turnkey's native signTransaction method
  // This is the CORRECT approach - uses TRANSACTION_TYPE_SOLANA for proper Solana transaction signing
  // CRITICAL: Turnkey expects base58 encoding for Solana transactions, NOT base64
  const signTransaction = useCallback(async (tx: Transaction | VersionedTransaction): Promise<Transaction | VersionedTransaction> => {
    if (!solanaWalletAccount || !publicKey) {
      throw new Error('Solana wallet not ready yet. Please wait a moment and try again.');
    }

    addBreadcrumb('Transaction signing started (native)', 'transaction', {
      isVersioned: tx instanceof VersionedTransaction,
      publicKey: publicKey.toBase58().slice(0, 8),
    });

    // Serialize the transaction - Turnkey expects HEX encoding for Solana transactions
    // The error "Encoding hex, invalid byte" confirms Turnkey's backend parses as hex
    const serializedTx = tx.serialize({ 
      requireAllSignatures: false,
      verifySignatures: false,
    });
    
    // CRITICAL FIX: Use HEX encoding (lowercase, no 0x prefix)
    // Turnkey's backend explicitly expects hex despite Solana's canonical base64
    const unsignedTxHex = Buffer.from(serializedTx).toString('hex');

    DEBUG && console.log('[TurnkeyProvider] signTransaction input:', {
      encoding: 'hex',
      txBytesLength: serializedTx.length,
      txHexLength: unsignedTxHex.length,
      first40Chars: unsignedTxHex.slice(0, 40),
      // Verify it's valid hex (only 0-9, a-f)
      isValidHex: /^[0-9a-f]+$/i.test(unsignedTxHex),
    });

    addBreadcrumb('Sending to Turnkey for native signing', 'transaction', {
      txLengthBytes: serializedTx.length,
      txLengthHex: unsignedTxHex.length,
    });

    let signedTxHex: string;
    try {
      // Use Turnkey's native signTransaction with TRANSACTION_TYPE_SOLANA
      // This returns a fully signed transaction in hex
      const result = await turnkeySignTransaction({
        walletAccount: solanaWalletAccount,
        unsignedTransaction: unsignedTxHex,
        transactionType: 'TRANSACTION_TYPE_SOLANA',
      });
      
      // Defensive check for undefined response
      if (!result) {
        throw new Error('Turnkey returned empty response for transaction signing');
      }
      signedTxHex = result;
      
      DEBUG && console.log('[TurnkeyProvider] signTransaction output:', {
        signedTxLength: signedTxHex.length,
        first40Chars: signedTxHex.slice(0, 40),
        // Check if response is hex or base64
        looksLikeHex: /^[0-9a-f]+$/i.test(signedTxHex),
        looksLikeBase64: /^[A-Za-z0-9+/=]+$/.test(signedTxHex),
      });
      
      addBreadcrumb('Turnkey native signing successful', 'transaction', {
        signedTxLength: signedTxHex.length,
      });
    } catch (signError) {
      console.error('[TurnkeyProvider] signTransaction error:', {
        error: signError,
        message: signError instanceof Error ? signError.message : 'Unknown',
        unsignedTxSample: unsignedTxHex.slice(0, 80),
      });
      addBreadcrumb('Turnkey native signing failed', 'error', {
        error: signError instanceof Error ? signError.message : 'Unknown error',
      });
      captureError(signError instanceof Error ? signError : new Error(String(signError)), {
        context: 'turnkey_sign_transaction',
      });
      throw signError;
    }

    // Decode the signed transaction - try hex first, fall back to base64 if needed
    let signedBytes: Uint8Array;
    if (/^[0-9a-f]+$/i.test(signedTxHex)) {
      // Response is hex encoded
      signedBytes = Buffer.from(signedTxHex, 'hex');
      DEBUG && console.log('[TurnkeyProvider] Decoded from hex');
    } else {
      // Response might be base64 encoded
      signedBytes = Buffer.from(signedTxHex, 'base64');
      DEBUG && console.log('[TurnkeyProvider] Decoded from base64');
    }
    
    DEBUG && console.log('[TurnkeyProvider] Decoded signed transaction:', {
      signedBytesLength: signedBytes.length,
    });
    
    // Reconstruct the transaction from the signed bytes
    let signedTx: Transaction | VersionedTransaction;
    if (tx instanceof VersionedTransaction) {
      signedTx = VersionedTransaction.deserialize(signedBytes);
    } else {
      signedTx = Transaction.from(signedBytes);
    }

    addBreadcrumb('Transaction signed successfully (native)', 'transaction', {
      signedBytesLength: signedBytes.length,
    });

    return signedTx;
  }, [solanaWalletAccount, publicKey, turnkeySignTransaction]);

  const signAllTransactions = useCallback(async (txs: (Transaction | VersionedTransaction)[]) => {
    const signed = [] as (Transaction | VersionedTransaction)[];
    for (const tx of txs) {
      signed.push(await signTransaction(tx));
    }
    return signed;
  }, [signTransaction]);

  // Send a transaction by signing it and broadcasting via the provided connection
  const sendTransaction = useCallback(async (tx: Transaction | VersionedTransaction, connection?: unknown): Promise<string> => {
    if (!solanaWalletAccount || !publicKey) {
      throw new Error('Solana wallet not ready yet. Please wait a moment and try again.');
    }

    addBreadcrumb('sendTransaction started', 'transaction', {
      isVersioned: tx instanceof VersionedTransaction,
      hasConnection: !!connection,
    });

    // Sign the transaction
    const signedTx = await signTransaction(tx);

    // Serialize the signed transaction
    const signedBytes = signedTx instanceof VersionedTransaction
      ? signedTx.serialize()
      : signedTx.serialize();

    addBreadcrumb('Transaction serialized for broadcast', 'transaction', {
      byteLength: signedBytes.length,
    });

    // Broadcast via the provided Connection object (standard Helius RPC).
    // NOTE: Helius Sender is intentionally NOT used here. Sender is for
    // latency-sensitive trading (buy/sell) only. Token creation goes through
    // standard RPC. The Sender was incorrectly added to this path in 3131552
    // and caused "account already in use" failures when Sender returned 500
    // but still landed the transaction on-chain.
    DEBUG && console.log('[TurnkeyProvider] Broadcasting via RPC:', {
      hasConnection: !!connection,
      connectionType: connection ? connection.constructor?.name : 'undefined',
      hasSendRawTransaction: connection ? typeof (connection as any).sendRawTransaction === 'function' : false,
    });

    if (connection && typeof (connection as any).sendRawTransaction === 'function') {
      try {
        const signature = await (connection as any).sendRawTransaction(signedBytes);
        addBreadcrumb('Transaction broadcast via RPC', 'transaction', {
          method: 'rpc',
          signature: typeof signature === 'string' ? signature.slice(0, 16) : 'unknown',
        });
        return signature;
      } catch (broadcastError) {
        addBreadcrumb('Transaction broadcast failed', 'error', {
          error: broadcastError instanceof Error ? broadcastError.message : 'Unknown error',
        });
        captureError(broadcastError instanceof Error ? broadcastError : new Error(String(broadcastError)), {
          feature: 'rpc_broadcast',
        });
        throw broadcastError;
      }
    }

    addBreadcrumb('Transaction broadcast failed - no connection', 'error');
    throw new Error('Transaction sending not available - no connection provided.');
  }, [solanaWalletAccount, publicKey, signTransaction]);

  // Send a trading transaction via Helius Sender for MEV protection + Jito dual routing.
  // For buy/sell trades ONLY. Token creation uses sendTransaction (standard RPC).
  //
  // On Sender HTTP 500: return pre-extracted tx signature (tx may have landed on-chain).
  // On Sender unreachable: fall back to standard sendRawTransaction via connection.
  const sendTradingTransaction = useCallback(async (tx: Transaction | VersionedTransaction, connection?: unknown): Promise<string> => {
    if (!solanaWalletAccount || !publicKey) {
      throw new Error('Solana wallet not ready yet. Please wait a moment and try again.');
    }

    addBreadcrumb('sendTradingTransaction started', 'transaction', {
      isVersioned: tx instanceof VersionedTransaction,
      hasConnection: !!connection,
      rpc_provider: 'helius_sender',
      feature: 'helius_sender',
    });

    // Step 1: Sign the transaction via Turnkey
    const signedTx = await signTransaction(tx);

    // Step 2: Serialize the signed transaction
    const signedBytes = signedTx.serialize();
    const signedBase64 = Buffer.from(signedBytes).toString('base64');

    // Step 3: Extract tx signature BEFORE broadcasting.
    // The first signature in the signed transaction IS the tx ID.
    // This allows us to return the signature even if Sender returns 500.
    let preExtractedSignature: string;
    if (signedTx instanceof VersionedTransaction) {
      preExtractedSignature = bs58.encode(signedTx.signatures[0]);
    } else {
      if (!signedTx.signature) {
        throw new Error('Signed transaction has no signature');
      }
      preExtractedSignature = bs58.encode(signedTx.signature);
    }

    addBreadcrumb('Trading tx signed, attempting Helius Sender', 'transaction', {
      byteLength: signedBytes.length,
      signature: preExtractedSignature.slice(0, 16),
      rpc_provider: 'helius_sender',
      feature: 'helius_sender',
    });

    // Step 4: Try Helius Sender first
    try {
      const senderSignature = await sendViaSender(signedBase64);

      addBreadcrumb('Trading tx broadcast via Helius Sender', 'transaction', {
        event: 'sender_success',
        signature: senderSignature.slice(0, 16),
        rpc_provider: 'helius_sender',
        feature: 'helius_sender',
      });

      return senderSignature;
    } catch (senderError) {
      const senderMsg = senderError instanceof Error ? senderError.message : String(senderError);
      const isSender500 = senderMsg.includes('Sender HTTP error: 500');

      if (isSender500) {
        // HTTP 500: TX may have been forwarded before the server error.
        // Return the pre-extracted signature so confirmation polling can check.
        addBreadcrumb('Helius Sender returned 500, returning pre-extracted signature', 'transaction', {
          event: 'sender_500_returning_sig',
          signature: preExtractedSignature.slice(0, 16),
          error: senderMsg,
          rpc_provider: 'helius_sender',
          feature: 'helius_sender',
        });

        captureError(
          senderError instanceof Error ? senderError : new Error(senderMsg),
          {
            feature: 'helius_sender',
            rpc_provider: 'helius_sender',
            event: 'sender_500',
            signature: preExtractedSignature.slice(0, 16),
          }
        );

        return preExtractedSignature;
      }

      // Non-500 error (unreachable, timeout, DNS, 4xx, JSON-RPC error):
      // TX was NOT sent via Sender. Fall back to standard RPC.
      addBreadcrumb('Helius Sender unreachable, falling back to RPC', 'transaction', {
        event: 'sender_fallback',
        error: senderMsg,
        rpc_provider: 'helius_sender',
        feature: 'helius_sender',
      });

      captureError(
        senderError instanceof Error ? senderError : new Error(senderMsg),
        {
          feature: 'helius_sender',
          rpc_provider: 'helius_sender',
          event: 'sender_fallback',
        }
      );

      // Fallback: broadcast via standard RPC connection
      if (connection && typeof (connection as any).sendRawTransaction === 'function') {
        try {
          const rpcSignature = await (connection as any).sendRawTransaction(signedBytes);

          addBreadcrumb('Trading tx broadcast via RPC fallback', 'transaction', {
            event: 'rpc_fallback_success',
            signature: typeof rpcSignature === 'string' ? rpcSignature.slice(0, 16) : 'unknown',
            rpc_provider: 'rpc_fallback',
            feature: 'helius_sender',
          });

          return rpcSignature;
        } catch (rpcError) {
          addBreadcrumb('RPC fallback also failed', 'error', {
            event: 'rpc_fallback_failed',
            error: rpcError instanceof Error ? rpcError.message : 'Unknown error',
            rpc_provider: 'rpc_fallback',
            feature: 'helius_sender',
          });

          captureError(
            rpcError instanceof Error ? rpcError : new Error(String(rpcError)),
            {
              feature: 'helius_sender',
              rpc_provider: 'rpc_fallback',
              event: 'rpc_fallback_failed',
            }
          );

          throw rpcError;
        }
      }

      throw new Error('Transaction sending not available - Helius Sender failed and no connection for fallback.');
    }
  }, [solanaWalletAccount, publicKey, signTransaction]);

  // Export wallet (reveal private key) using Turnkey's secure iframe flow
  const exportWallet = useCallback(async () => {
    if (!solanaAddress) {
      throw new Error('No Solana wallet to export');
    }
    
    DEBUG && console.log('[TurnkeyProvider] Initiating wallet export for:', solanaAddress);
    
    await handleExportWalletAccount({
      address: solanaAddress,
      keyFormat: KeyFormat.Solana,
    });
  }, [solanaAddress, handleExportWalletAccount]);

  // Import wallet (import existing private key) using Turnkey's secure iframe flow
  const importWallet = useCallback(async () => {
    DEBUG && console.log('[TurnkeyProvider] Initiating wallet import');
    addBreadcrumb('Wallet import initiated', 'wallet');
    
    const walletId = await handleImportPrivateKey({
      curve: 'CURVE_ED25519',
      addressFormats: ['ADDRESS_FORMAT_SOLANA'],
      keyName: 'Imported Solana Wallet',
      successPageDuration: 2000,
    });
    
    // Refresh wallets to pick up the newly imported wallet
    await refreshWallets();
    
    addBreadcrumb('Wallet import completed', 'wallet', { walletId });
    return walletId;
  }, [handleImportPrivateKey, refreshWallets]);

  // Subscribe to wallet changes (for cache invalidation)
  const onWalletChange = useCallback((callback: () => void) => {
    setWalletChangeCallbacks(prev => {
      const next = new Set(prev);
      next.add(callback);
      return next;
    });
    
    // Return unsubscribe function
    return () => {
      setWalletChangeCallbacks(prev => {
        const next = new Set(prev);
        next.delete(callback);
        return next;
      });
    };
  }, []);

  // Notify subscribers when wallet changes
  const prevSolanaAddressRef = useRef<string | null>(null);
  useEffect(() => {
    if (prevSolanaAddressRef.current !== solanaAddress && prevSolanaAddressRef.current !== null) {
      DEBUG && console.log('[TurnkeyProvider] Wallet changed, notifying subscribers');
      addBreadcrumb('Wallet changed', 'wallet', {
        previousAddress: prevSolanaAddressRef.current?.slice(0, 8),
        newAddress: solanaAddress?.slice(0, 8)
      });
      walletChangeCallbacks.forEach(cb => cb());
    }
    prevSolanaAddressRef.current = solanaAddress;
  }, [solanaAddress, walletChangeCallbacks]);

  // Register wallet with Helius for instant deposit notifications
  const registerWalletForHeliusTracking = useCallback(async (walletAddress: string) => {
    try {
      // Check if already tracked (using session storage to avoid repeated DB calls)
      const trackingKey = `helius_tracked_${walletAddress}`;
      if (sessionStorage.getItem(trackingKey)) {
        DEBUG && console.log('[TurnkeyProvider] Wallet already tracked by Helius (cached)');
        return;
      }

      // Check if already tracked in database
      const { data: profile } = await supabase
        .from('creator_profiles')
        .select('helius_tracked')
        .eq('wallet_address', walletAddress)
        .maybeSingle();

      if (profile?.helius_tracked) {
        DEBUG && console.log('[TurnkeyProvider] Wallet already tracked by Helius');
        sessionStorage.setItem(trackingKey, 'true');
        return;
      }

      DEBUG && console.log('[TurnkeyProvider] Registering wallet for Helius tracking:', walletAddress.slice(0, 8));
      addBreadcrumb('Helius tracking registration started', 'wallet', { address: walletAddress.slice(0, 8) });

      const { error } = await supabase.functions.invoke('helius-manage-addresses', {
        body: {
          action: 'add',
          addresses: [walletAddress]
        }
      });

      if (error) {
        console.error('[TurnkeyProvider] Failed to register wallet for Helius tracking:', error);
        addBreadcrumb('Helius tracking registration failed', 'error', { error: error.message });
        return;
      }

      // Mark as tracked in database
      await supabase
        .from('creator_profiles')
        .update({
          helius_tracked: true,
          helius_tracked_at: new Date().toISOString()
        })
        .eq('wallet_address', walletAddress);

      sessionStorage.setItem(trackingKey, 'true');
      DEBUG && console.log('[TurnkeyProvider] Wallet registered for instant notifications');
      addBreadcrumb('Helius tracking registration complete', 'wallet', { address: walletAddress.slice(0, 8) });
    } catch (err) {
      console.error('[TurnkeyProvider] Error registering wallet for Helius:', err);
      captureError(err instanceof Error ? err : new Error(String(err)), {
        feature: 'helius_registration',
      });
      // Non-critical - notifications will fall back to polling
    }
  }, []);

  // Track wallet connection state changes and set Sentry user
  useEffect(() => {
    if (isAuthenticated && solanaAddress) {
      addBreadcrumb('Wallet connected', 'wallet', { address: solanaAddress.slice(0, 8) });
      setUser(solanaAddress);

      // Log user session for protocol stats (rate limited to once per session)
      const sessionKey = `session_logged_${solanaAddress}`;
      if (!sessionStorage.getItem(sessionKey)) {
        supabase.from('user_sessions').insert({
          wallet_address: solanaAddress,
          user_agent: navigator.userAgent,
        }).then(({ error }: { error: { message: string } | null }) => {
          if (error) {
            console.warn('[TurnkeyProvider] Failed to log session:', error);
            captureError(new Error(error.message), { feature: 'session_logging' });
          } else {
            sessionStorage.setItem(sessionKey, 'true');
            DEBUG && console.log('[TurnkeyProvider] User session logged');
          }
        });
      }

      // Register wallet with Helius for instant deposit notifications
      registerWalletForHeliusTracking(solanaAddress);
    } else {
      clearUser();
    }
  }, [isAuthenticated, solanaAddress, registerWalletForHeliusTracking]);

  // Log state for debugging
  useEffect(() => {
    DEBUG && console.log('[TurnkeyProvider] Auth state:', {
      authState,
      isAuthenticated,
      isConfigured,
      solanaAddress,
      walletsCount: wallets?.length || 0,
      user: user?.userName,
      hasSolanaWalletAccount: !!solanaWalletAccount,
      hasPublicKey: !!publicKey,
    });
  }, [authState, isAuthenticated, isConfigured, solanaAddress, wallets, user, solanaWalletAccount, publicKey]);

  const contextValue: TurnkeyWalletContextType = {
    isAuthenticated,
    isLoading,
    isConfigured,
    publicKey,
    walletAddress: solanaAddress,
    connected: isAuthenticated && !!solanaWalletAccount && !!publicKey,
    allSolanaWallets,
    activeWalletIndex,
    setActiveWallet,
    login,
    logout,
    signTransaction: isAuthenticated ? signTransaction : undefined,
    signAllTransactions: isAuthenticated ? signAllTransactions : undefined,
    signMessage: isAuthenticated ? signMessage : undefined,
    sendTransaction: isAuthenticated ? sendTransaction : undefined,
    sendTradingTransaction: isAuthenticated ? sendTradingTransaction : undefined,
    exportWallet: isAuthenticated && solanaAddress ? exportWallet : undefined,
    importWallet: isAuthenticated ? importWallet : undefined,
    onWalletChange: isAuthenticated ? onWalletChange : undefined,
  };

  return (
    <TurnkeyWalletContext.Provider value={contextValue}>
      {children}
    </TurnkeyWalletContext.Provider>
  );
};

// Helper function to decode signature component (handles hex, base64, and base64url)
function decodeSignatureComponent(component: string): Uint8Array {
  // Handle 0x-prefixed hex
  if (component.startsWith('0x')) {
    return hexToBytes(component.slice(2));
  }
  // Check if it looks like hex (only 0-9, a-f characters and even length)
  if (/^[0-9a-fA-F]+$/.test(component) && component.length % 2 === 0) {
    return hexToBytes(component);
  }
  
  // Try base64/base64url as fallback
  try {
    // Convert base64url to base64 (replace - with +, _ with /)
    let base64 = component.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if needed
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    console.error('[decodeSignatureComponent] base64 decode failed:', e);
    // If base64 fails, assume it's hex without 0x prefix
    return hexToBytes(component);
  }
}

// Helper function to convert hex string to Uint8Array
function hexToBytes(hex: string): Uint8Array {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(cleanHex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}


interface TurnkeyProviderProps {
  children: ReactNode;
}

export const TurnkeyProvider: FC<TurnkeyProviderProps> = ({ children }) => {
  // Get config from single source of truth
  const config = getTurnkeyPublicConfig();
  const missingKeys = getTurnkeyMissingKeys();
  const isConfigured = checkTurnkeyConfigured();
  
  // Startup diagnostics logging - single structured line
  useEffect(() => {
    const diagnostics = getTurnkeyDiagnostics();
    DEBUG && console.log('[TurnkeyProvider] Config:', {
      origin: diagnostics.origin,
      redirectUri: diagnostics.redirectUri,
      hasOrgId: diagnostics.hasOrganizationId,
      hasAuthProxyConfigId: diagnostics.hasAuthProxyConfigId,
      isConfigured: diagnostics.isConfigured,
      missingKeys: diagnostics.missingKeys,
      hasRuntimeOverride: diagnostics.hasRuntimeOverride,
    });
    
    // Show explicit toast if not configured
    if (!isConfigured) {
      const missingList = missingKeys.join(', ');
      toast.error('Sign-in unavailable', {
        description: `Missing: ${missingList}. Set them in Lovable Environment Variables and redeploy.`,
        duration: 15000,
        action: {
          label: 'Open diagnostics',
          onClick: () => window.location.href = '/auth/diagnostics',
        },
      });
    }
  }, [isConfigured, missingKeys]);

  // Full dextoro logo for Turnkey modal - using Supabase storage for external SDK access
  const logoUrl = 'https://vkhijzofpanjuolcdazv.supabase.co/storage/v1/object/public/assets/dextoro%20logo%20turnkey%20modal.png';

  const turnkeyConfig: TurnkeyProviderConfig = useMemo(() => ({
    organizationId: config.organizationId || '',
    authProxyConfigId: config.authProxyConfigId || '',
    auth: {
      oauthConfig: {
        // Use popup flow for more reliable SPA behavior
        openOauthInPage: false,
        oauthRedirectUri: config.redirectUri,
      },
      // Automatically refresh session before expiry
      autoRefreshSession: true,
      createSuborgParams: {
        emailOtpAuth: defaultSuborgParams,
        smsOtpAuth: defaultSuborgParams,
        passkeyAuth: defaultSuborgParams,
        oauth: defaultSuborgParams,
      },
    },
    ui: {
      // Enable dark mode to match app theme
      darkMode: true,
      // Custom logo for branding
      logoLight: logoUrl,
      logoDark: logoUrl,
      // Rounded corners to match app style
      borderRadius: 12,
      // Color customization for dark mode - use hex colors for iframe compatibility
      colors: {
        dark: {
          // Use app's primary color (neon green) - exact brand color
          primary: '#CCFF00', // dextoro brand neon green
          primaryText: '#000000',
          // Dark modal background
          modalBackground: '#1a1a1a', // matches --background
          modalText: '#ffffff',
          // Button styling
          button: '#333333',
          // Icon backgrounds
          iconBackground: '#404040',
          iconText: '#a3a3a3',
          // Status colors
          success: '#22c55e', // green-500
          danger: '#ef4444', // red-500
        },
      },
    },
  }), [config.organizationId, config.authProxyConfigId, config.redirectUri, logoUrl]);

  // TODO(sentry): DEXTORO-LAUNCH-90 - Chrome 132 dynamic import failure during Turnkey init. Monitor for recurrence.
  return (
    <TurnkeySDKProvider
      config={turnkeyConfig}
      callbacks={{
        onError: (error: Error & { code?: string; name?: string; cause?: unknown }) => {
          const cause = (error as any)?.cause as unknown;
          const causeMessage =
            typeof cause === 'string'
              ? cause
              : cause && typeof cause === 'object' && 'message' in (cause as any)
                ? String((cause as any).message)
                : null;

          const errorMsg = `${error?.message ?? ''} ${causeMessage ?? ''}`.toLowerCase();

          // Suppress expected session errors - these happen when:
          // - User's session expired and auto-refresh failed
          // - User tries to logout when already logged out
          // - Browser was closed/reopened after session expired
          const isExpectedSessionError =
            errorMsg.includes('no active session') ||
            errorMsg.includes('failed to refresh session') ||
            errorMsg.includes('session expired') ||
            errorMsg.includes('session not found');

          if (isExpectedSessionError) {
            // Log at warn level, not error (won't trigger Sentry)
            console.warn('[TurnkeyProvider] Session expired or not found (expected):', error?.message);
            // Don't show toast for expected session errors - user just needs to log in again
            return;
          }

          // Suppress expected OTP verification failures (user entered wrong code)
          const isExpectedOtpError =
            errorMsg.includes('failed to verify otp') ||
            errorMsg.includes('invalid otp') ||
            errorMsg.includes('otp expired');

          if (isExpectedOtpError) {
            console.warn('[TurnkeyProvider] OTP verification failed (expected):', error?.message);
            toast.error('Verification failed', {
              description: 'The code you entered is invalid or expired. Please try again.',
              duration: 6000,
            });
            return;
          }

          console.error('[TurnkeyProvider] Auth error:', {
            name: error?.name,
            message: error?.message,
            code: (error as any)?.code,
            causeMessage,
          });

          captureError(error, {
            context: 'turnkey_auth',
            errorCode: (error as any)?.code,
          });

          const smsHint =
            errorMsg.includes('sms') || errorMsg.includes('otp')
              ? 'SMS OTP appears disabled or not permitted for this Turnkey configuration (often due to plan/policy). Try Email/Passkey, and enable SMS + OTP permissions in Turnkey.'
              : 'If this persists, update Turnkey Allowed Origins and Redirect URL for this domain.';

          toast.error('Sign-in failed', {
            description: causeMessage ? `${smsHint} (${causeMessage})` : smsHint,
            duration: 12000,
          });
        },
        onAuthenticationSuccess: ({ session }) => {
          DEBUG && console.log('[TurnkeyProvider] Authentication successful:', {
            organizationId: session?.organizationId,
          });
          toast.success('Signed in successfully!');
        },
      }}
    >
      <TurnkeyWalletInner>
        {children}
      </TurnkeyWalletInner>
    </TurnkeySDKProvider>
  );
};

// Custom hook to use the Turnkey wallet context
// Returns a safe default if called outside the provider (for SSR/testing/initialization)
export function useTurnkeyWallet(): TurnkeyWalletContextType {
  const context = useContext(TurnkeyWalletContext);
  
  // Return safe defaults if context is not available
  // This can happen during initialization or if TurnkeyProvider fails to mount
  if (!context) {
    return {
      isAuthenticated: false,
      isLoading: true,
      isConfigured: isTurnkeyConfigured(),
      publicKey: null,
      walletAddress: null,
      connected: false,
      allSolanaWallets: [],
      activeWalletIndex: 0,
      setActiveWallet: () => console.warn('[TurnkeyWallet] Provider not initialized'),
      login: () => console.warn('[TurnkeyWallet] Provider not initialized'),
      logout: async () => console.warn('[TurnkeyWallet] Provider not initialized'),
      signTransaction: undefined,
      signAllTransactions: undefined,
      signMessage: undefined,
      sendTransaction: undefined,
      exportWallet: undefined,
      importWallet: undefined,
      onWalletChange: undefined,
    };
  }
  
  return context;
}

// Re-export AuthState for use in components
export { AuthState };

export default TurnkeyProvider;
